/**
 * @module Shop
 * @description Shop Page
 */
import { useContext } from "react";
import Head from "next/head";
import { ContentProviderContext } from "@/providers/content-provider";
import valueProps from "@/blocks/ValueProps/data";
import Link from "next/link";
import Tooltip from "@/blocks/Tooltip";
import { trackEvent } from "@/js/helpers/tracking";

// Template
import Template from "@/templates/Default";

// Blocks
import BannerServiceCheckSingleLine from "@/blocks/Banners/BannerServiceCheckSingleLine";
import HeroPricePoint from "@/blocks/Heroes/HeroPricePoint";
import BannerList from "@/blocks/Banners/BannerList";
import ComparisonGrid from "@/blocks/ComparisonGrid";
import BannerCTA from "@/blocks/Banners/BannerCTA";
import FAQs from "@/blocks/FAQS/ShopFAQS";
import ExistingCustomerModal from "@/blocks/Modals/ExistingCustomerModal";

// Cohesion
import MonarchFallback from "@/blocks/MonarchFallback";
import { MonarchSlot } from "@redventures/cohesion-utils-react";

// Schemas
import { fiberProductSchemas } from "@/js/utils/schema";

/**
 * @function Page
 * @description Page
 */
const Page = () => {
  const { featuredProduct } = useContext(ContentProviderContext);

  /**
   * @function handleClick
   * @description
   */
  const handleClick = (event) => {
    const { target } = event;
    const text = target.innerHTML;
    const data = {
      elementType: "LINK",
      location: "SHOP",
      position: "COMPARISON GRID",
      text,
      actionOutcome: "INTERNALLINK",
    };

    trackEvent({ action: "elementClicked", data, event });
  };

  return (
    <>
      <Head>
        <title>Frontier Internet Plans: Speeds, Pricing & Availability</title>
        <meta
          name="description"
          content="Shop Frontier Internet Plans for fast, reliable, high-speed connectivity. Find the perfect fiber plan to match your needs and stay connected effortlessly."
        />
        <link rel="canonical" href="https://frontier.com/shop" />
      </Head>

      <Template
        title="Shop Frontier Services"
        modal={<ExistingCustomerModal />}
        hero={
          <>
            {/* Shop | Banner */}
            <MonarchSlot
              slotId="frdcShopBanner"
              fallback={<MonarchFallback height={["88px", "82px", "88px"]} />}
            >
              <BannerServiceCheckSingleLine />
            </MonarchSlot>

            {/* Shop | Hero */}
            <MonarchSlot
              slotId="frdcShopHero"
              fallback={
                <MonarchFallback height={["716px", "488px", "570px"]} />
              }
            >
              <HeroPricePoint
                headline={
                  <>
                    Better <span>faster</span>{" "}
                    <span key="span-1" className="u-color-blue">
                      100% FIBER
                    </span>
                  </>
                }
                useSelectAreasLegal
              />
            </MonarchSlot>
          </>
        }
        primary={
          <>
            {/* Shop | Section 1 */}
            <MonarchSlot
              slotId="frdcShopSection1"
              fallback={
                <MonarchFallback height={["1288px", "1244px", "1200px"]} />
              }
            >
              <BannerList
                theme="blue"
                items={valueProps?.[featuredProduct?.hash]}
              />
            </MonarchSlot>

            {/* Shop | Section 2 */}
            <MonarchSlot
              slotId="frdcShopSection2"
              fallback={
                <MonarchFallback height={["360px", "304px", "455px"]} />
              }
            ></MonarchSlot>

            {/* Shop | Section 3 */}
            <MonarchSlot slotId="frdcShopSection3">
              <ComparisonGrid
                rows={[
                  {
                    title: (
                      <>
                        <Link
                          href="/shop/internet/fiber-internet"
                          onClick={handleClick}
                        >
                          Experience fiber-fast
                        </Link>
                      </>
                    ),
                    copy: "Access our 100% fiber-optic network",
                    first: true,
                    second: false,
                  },
                  {
                    title: (
                      <>
                        <Link
                          href="https://frontier.com/shop/internet/fiber-internet/gig"
                          target="_blank"
                          rel="noreferrer"
                          onClick={handleClick}
                        >
                          Go 3x faster
                        </Link>
                      </>
                    ),
                    copy: (
                      <>
                        Experience upload speeds 3x faster than cable
                        <span
                          key="span-1"
                          data-legal="Gig-25x-faster-than-cable"
                        />
                      </>
                    ),
                    first: true,
                    second: false,
                  },
                  {
                    title: (
                      <>
                        <Link href="/" onClick={handleClick}>
                          Unlock unlimited access
                        </Link>
                      </>
                    ),
                    copy: (
                      <>
                        Stream, work and game with no data caps or overage
                        charges
                        <Tooltip disclosure="data-caps" />
                      </>
                    ),
                    first: true,
                    second: false,
                  },
                  {
                    title: (
                      <>
                        <Link href="/" onClick={handleClick}>
                          Game with less lag
                        </Link>
                      </>
                    ),
                    copy: (
                      <>
                        Fiber has 2.9x less latency than cable
                        <Tooltip disclosure="less-latency" />
                      </>
                    ),
                    first: true,
                    second: false,
                  },
                  {
                    title: (
                      <>
                        <Link
                          href="https://frontier.com/helpcenter/billing/how-to-manage-auto-pay"
                          target="_blank"
                          rel="noreferrer"
                          onClick={handleClick}
                        >
                          Save with Auto Pay
                        </Link>
                      </>
                    ),
                    copy: "Save $10 on your monthly bill when you establish and maintain Auto Pay",
                    first: true,
                    second: true,
                  },
                ]}
              />
            </MonarchSlot>

            {/* Shop | Section 4 */}
            <MonarchSlot slotId="frdcShopSection4">
              <BannerCTA copy="Ready to find internet plans available in your neighborhood?" />
            </MonarchSlot>

            {/* Shop | Section 5 */}
            <MonarchSlot
              slotId="frdcShopSection5"
              fallback={<MonarchFallback height={["0px", "0px", "0px"]} />}
            />

            {/* Shop | Section 6 */}
            <MonarchSlot slotId="frdcShopSection6">
              <FAQs />
            </MonarchSlot>

            {/* Shop | Section 7 */}
            <MonarchSlot slotId="frdcShopSection7"></MonarchSlot>

            {/* Shop | Section 8 */}
            <MonarchSlot slotId="frdcShopSection8"></MonarchSlot>

            {/* Shop | Section 9 */}
            <MonarchSlot slotId="frdcShopSection9"></MonarchSlot>

            {/* Shop | Sticky CTA */}
            <MonarchSlot slotId="frdcShopStickyCta"></MonarchSlot>
          </>
        }
      ></Template>
      {fiberProductSchemas().map((schema, index) => (
        <script
          key={`${schema}${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${schema}`,
          }}
        />
      ))}
    </>
  );
};

export default Page;
